import { ref, type Ref } from 'vue'

export interface DragDropOptions {
  onFiles: (files: File[]) => void
  accept?: string[]
}

export interface UseDragDropReturn {
  isDragging: Ref<boolean>
  onDragOver: (e: DragEvent) => void
  onDragLeave: (e: DragEvent) => void
  onDrop: (e: DragEvent) => void
}

/**
 * Composable for handling drag-and-drop file operations.
 *
 * @param options.onFiles - Callback receiving dropped files
 * @param options.accept - Optional array of accepted file extensions (e.g. ['.csv', '.txt'])
 */
export function useDragDrop(options: DragDropOptions): UseDragDropReturn {
  const { onFiles, accept } = options
  const isDragging = ref(false)

  const onDragOver = (e: DragEvent) => {
    e.preventDefault()
    isDragging.value = true
  }

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault()
    isDragging.value = false
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    isDragging.value = false

    const dataTransfer = e.dataTransfer
    if (!dataTransfer?.files?.length) return

    let files = Array.from(dataTransfer.files)

    // Filter by accepted extensions if specified
    if (accept && accept.length > 0) {
      const extensions = accept.map((ext) => ext.toLowerCase())
      files = files.filter((file) => {
        const fileName = file.name.toLowerCase()
        return extensions.some((ext) => fileName.endsWith(ext))
      })
    }

    if (files.length > 0) {
      onFiles(files)
    }
  }

  return {
    isDragging,
    onDragOver,
    onDragLeave,
    onDrop
  }
}
